import { memo, MouseEvent, useCallback } from 'react'
import { ButtonBase, SxProps } from '@mui/material'
import clsx from 'clsx'
import { ChevronDownIcon, CrossIcon } from '@/assets'
import { globalClasses } from '@/styles'
import { Box } from '@/components/Layout'
import Text from '../../Text'
import { dropdownButtonStyles as styles } from './DropdownButton.style'
import { DropdownButtonProps } from './DropdownButton.types'
import { dropdownButtonClasses } from './DropdownButton.data'

function DropdownButton({
  sx,
  label,
  labelProps = {},
  labelHeading,
  labelHeadingProps = {},
  className = '',
  iconWrapperProps = {},
  active = false,
  fullWidth = false,
  fullWidthWithSpace = false,
  enableClear = false,
  onClear,
  ...props
}: DropdownButtonProps) {
  const { sx: labelSx, className: labelClassName = '', ...otherLabelProps } = labelProps
  const { sx: labelHeadingSx, ...otherLabelHeadingProps } = labelHeadingProps
  const { sx: iconWrapperSx, ...otherIconWrapperProps } = iconWrapperProps

  const handleClearClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      onClear?.()
    },
    [onClear],
  )

  return (
    <ButtonBase
      sx={[styles.button, sx] as SxProps}
      className={clsx({
        [dropdownButtonClasses.active]: active,
        [dropdownButtonClasses.withHeading]: Boolean(labelHeading),
        [dropdownButtonClasses.fullWidth]: fullWidth,
        [dropdownButtonClasses.fullWidthWithSpace]: fullWidthWithSpace,
        [className]: Boolean(className),
      })}
      {...props}>
      <Box>
        {labelHeading && (
          <Text sx={[styles.labelHeading, labelHeadingSx] as SxProps} {...otherLabelHeadingProps}>
            {labelHeading}
          </Text>
        )}
        <Text
          sx={[styles.label, labelSx] as SxProps}
          className={clsx({
            [labelClassName]: Boolean(labelClassName),
            [globalClasses.ellipsis]: true,
          })}
          {...otherLabelProps}>
          {label}
        </Text>
      </Box>
      {enableClear && onClear ? (
        <Box
          sx={[styles.iconWrapper, iconWrapperSx] as SxProps}
          onClick={handleClearClick}
          {...otherIconWrapperProps}>
          <CrossIcon className={dropdownButtonClasses.icon} />
        </Box>
      ) : (
        <Box sx={[styles.iconWrapper, iconWrapperSx] as SxProps} {...otherIconWrapperProps}>
          <ChevronDownIcon className={dropdownButtonClasses.icon} />
        </Box>
      )}
    </ButtonBase>
  )
}

export default memo(DropdownButton)
